import { titleCase } from "./hooks";
import type { Field } from "../components/EntityForm";
import {
    participantQuickCreateFields, personLabel, companyCreateFields,
    contactCreateFields, contactOptionsOf,
} from "./formOptions";

/**
 * Shared referral intake logic — the mapping from a raw website form payload
 * onto the referral form, and the referral field definitions. Used both by the
 * Referrals page (its own intake queue) and the Enquiry queue, so a website
 * submission promoted from either place produces an identical referral. Pure
 * functions: no component state, safe to reuse anywhere.
 */

export const STATUS = ["new", "in_progress", "allocated", "active", "completed", "closed"]
    .map((s) => ({ value: s, label: titleCase(s) }));
export const URGENCY = ["non_urgent", "routine", "high", "urgent"]
    .map((s) => ({ value: s, label: titleCase(s) }));
export const RISK = ["low", "medium", "high", "critical"].map((s) => ({ value: s, label: titleCase(s) }));

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** Normalises payload keys (lowercased, punctuation to underscores). */
const normalisePayload = (raw: Record<string, unknown>): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw ?? {})) {
        payload[k.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")] = v;
    }
    return payload;
};

const pickFrom = (payload: Record<string, unknown>) => (...keys: string[]): string => {
    for (const key of keys) {
        const value = str(payload[key]);
        if (value) return value;
    }
    return "";
};

/** services arrives as an array, or a comma-joined string, or a single name. */
const servicesFromPayload = (payload: Record<string, unknown>): string[] => {
    const raw = payload.services ?? payload.support_type ?? payload.type_of_nursing_support_required;
    if (Array.isArray(raw)) return raw.map((s) => str(s)).filter(Boolean);
    return str(raw).split(",").map((s) => s.trim()).filter(Boolean);
};

const truthy = (v: string): boolean => ["yes", "on", "true", "1"].includes(v.toLowerCase());

/**
 * Best-effort mapping from a website form payload onto the referral form.
 *
 * Field names come from whatever the website's form inputs are called, so the
 * payload is normalised and each referral field tries every name the site's
 * forms have used (full_name / contact_phone / organisation_name, and the
 * agreed snake_case contract). Anything without a field of its own — NDIS
 * number, raw urgency wording, the consent declaration — is preserved in the
 * notes rather than dropped.
 */
export const prefillFromPayload = (raw: Record<string, unknown>): Record<string, unknown> => {
    const payload = normalisePayload(raw);
    const pick = pickFrom(payload);

    const participantName = pick("participant_name") ||
        `${pick("participant_first_name")} ${pick("participant_last_name")}`.trim();
    const noteBits = [
        participantName && `Participant: ${participantName}`,
        pick("ndis_number") && `NDIS number: ${pick("ndis_number")}`,
        pick("role_title") && `Referrer role: ${pick("role_title")}`,
        pick("urgency_level", "urgency") && `Urgency (as submitted): ${pick("urgency_level", "urgency")}`,
        pick("additional_notes", "message", "notes") &&
            `From the website form:\n${pick("additional_notes", "message", "notes")}`,
        truthy(pick("consent")) && "Consent: declared via the website form.",
    ].filter(Boolean).join("\n");

    const rawUrgency = pick("urgency_level", "urgency").toLowerCase();
    const urgency = rawUrgency.includes("non") ? "non_urgent"
        : rawUrgency.includes("urgent") ? "urgent"
        : rawUrgency.includes("high") ? "high"
        : rawUrgency.includes("routine") ? "routine" : "";

    const services = servicesFromPayload(payload);

    return {
        referring_contact_name: pick("referrer_name", "full_name", "name", "contact_name"),
        referring_contact_phone: pick("referrer_phone", "contact_phone", "phone"),
        referring_contact_email: pick("referrer_email", "contact_email", "email"),
        referring_organisation: pick("organisation_name", "organisation", "organization", "company", "provider_name"),
        referrer_relationship: pick("relationship", "relationship_to_participant"),
        ...(urgency ? { urgency } : {}),
        ...(services.length ? { services } : {}),
        additional_notes: noteBits,
    };
};

/**
 * Prefill for the nested "+ New ..." create forms during a promote: the
 * submission's organisation, referrer-as-contact and participant details land
 * in the respective create modals, so proper CRM records are one click each.
 */
export const nestedFromPayload = (raw: Record<string, unknown>) => {
    const payload = normalisePayload(raw);
    const pick = pickFrom(payload);
    const fullName = pick("referrer_name", "full_name", "name", "contact_name");
    const [firstName, ...rest] = fullName.split(/\s+/);
    return {
        company: {
            name: pick("organisation_name", "organisation", "organization", "company", "provider_name"),
            phone: pick("organisation_phone"),
            email: pick("organisation_email"),
            suburb: pick("organisation_suburb"),
            ndis_registered: truthy(pick("ndis_registered")),
        },
        contact: {
            first_name: firstName ?? "",
            last_name: rest.join(" "),
            job_title: pick("role_title"),
            email: pick("referrer_email", "contact_email", "email"),
            phone: pick("referrer_phone", "contact_phone", "phone"),
        },
        participant: {
            first_name: pick("participant_first_name") || pick("participant_name").split(/\s+/)[0] || "",
            last_name: pick("participant_last_name"),
            ndis_number: pick("ndis_number"),
        },
    };
};

export interface ReferralFieldDeps {
    companies: Array<{ id: string; name: string }>;
    contacts: Array<{ id: string; first_name: string; last_name: string; company_id: string | null }>;
    participants: Array<{ id: string; full_name: string }>;
    /** During a promote the nested "+ New ..." creates are prefilled from the payload. */
    nested?: ReturnType<typeof nestedFromPayload>;
}

/**
 * The referral form fields. The nested "+ New ..." creates can be prefilled —
 * during a promote they carry the website submission's organisation / referrer
 * / participant so proper CRM records are one click each.
 */
export const buildReferralFields = ({ companies, contacts, participants, nested }: ReferralFieldDeps): Field[] => [
    { name: "company_id", label: "Referring organisation (CRM record)", kind: "select", half: true,
      options: companies.map((c) => ({ value: c.id, label: c.name })),
      addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields(),
          initial: nested?.company } },
    { name: "contact_id", label: "Referring contact (CRM record)", kind: "select", half: true,
      cascadeFrom: "company_id",
      options: contactOptionsOf(contacts),
      addNew: { title: "New contact", path: "/contacts",
          fields: contactCreateFields(companies.map((c) => ({ value: c.id, label: c.name }))),
          optionLabel: personLabel,
          inherit: { company_id: "company_id" },
          initial: nested?.contact },
      help: "Links the CRM record; the typed referrer details below still travel with the referral." },
    { name: "referring_contact_name", label: "Referrer name", kind: "text", required: true, half: true },
    { name: "referring_contact_phone", label: "Referrer phone", kind: "tel", required: true, half: true },
    { name: "referring_organisation", label: "Organisation", kind: "text", half: true },
    { name: "referring_contact_email", label: "Referrer email", kind: "email", half: true },
    { name: "referrer_relationship", label: "Relationship to participant", kind: "text", half: true },
    { name: "participant_id", label: "Participant", kind: "select", half: true,
      options: participants.map((p) => ({ value: p.id, label: p.full_name })),
      addNew: { title: "New participant", path: "/participants",
          fields: participantQuickCreateFields(), optionLabel: personLabel,
          initial: nested?.participant },
      help: "A referral must be linked to a participant record before it can be accepted." },
    { name: "urgency", label: "Urgency", kind: "select", options: URGENCY, half: true },
    { name: "risk", label: "Risk", kind: "select", options: RISK, half: true },
    { name: "services", label: "Services requested", kind: "multiselect",
      optionsFrom: { path: "/referral-services", addLabel: "Add service" },
      help: "The clinical service list is managed in Settings — the website form mirrors it." },
    { name: "additional_notes", label: "Additional notes", kind: "textarea" },
];
